import { useContext, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { PaymentSource } from '../../constants';
import { reportError } from '../../lib/reportError';
import { useErrorModal } from '../../hooks/useErrorModal';
import { ADDRESSES_QUERY, AddressesQuery } from '../../hooks/useAddress';
import {
  usePaymentMethodsQuery,
  PAYMENT_METHODS_QUERY,
  PaymentMethodsQuery
} from '../../hooks/usePaymentMethods';
import {
  GET_ORDER_SUMMARY_QUERY,
  GetOrderSummaryQuery,
  GetOrderSummaryQueryVariables
} from '../../hooks/useOrderSummary';
import { useUpdateOrderPaymentMethod } from '../../hooks/useUpdateOrderPaymentMethod';
import { useUpdateOrderShippingAddress } from '../../hooks/useUpdateOrderShippingAddress';
import { useCountry } from '../CountrySelector';
import { useDialog } from '../Dialog';
import { useOrderDetails } from '../OrderDetails';
import { OrderDetailsPaymentSourceContext } from './OrderDetailsPaymentSourceProvider';

export const useOrderDetailsPaymentSource = () => {
  const client = useApolloClient();
  const { country } = useCountry();
  const { showDialog } = useDialog();
  const { showErrorModal } = useErrorModal();
  const { data: paymentMethods } = usePaymentMethodsQuery();
  const { orderNumber } = useOrderDetails();
  const { updateOrderPaymentMethod } = useUpdateOrderPaymentMethod(orderNumber);
  const { updateOrderShippingAddress } = useUpdateOrderShippingAddress(orderNumber);
  const { paymentSource, setPaymentSource } = useContext(OrderDetailsPaymentSourceContext);

  const changePaymentSource = useCallback(
    async (newPaymentSource: PaymentSource) => {
      if (paymentSource === newPaymentSource) return;

      const paymentMethod = paymentMethods?.paymentMethods.data.find(
        paymentMethod => paymentMethod.attributes.source === newPaymentSource
      );

      try {
        if (paymentMethod) {
          await updateOrderPaymentMethod(Number(paymentMethod.id));
        }
        setPaymentSource(newPaymentSource);
      } catch (error) {
        showErrorModal();
        reportError(error);
      }
    },
    [paymentMethods, updateOrderPaymentMethod, paymentSource, setPaymentSource, showErrorModal]
  );

  const selectLatestPaymentMethod = useCallback(async () => {
    // read the payment methods from the cache directly here instead of using the `usePaymentMethodsQuery` hook
    // to avoid race conditions where this is called right after the payment method is created
    const paymentMethods = client.readQuery<PaymentMethodsQuery>({
      query: PAYMENT_METHODS_QUERY
    });

    const paymentMethod = paymentMethods?.paymentMethods.data.find(
      paymentMethod => paymentMethod.attributes.source === paymentSource
    );

    try {
      if (!paymentMethod) throw new Error('selectLatestPaymentMethod: no payment method found');
      await updateOrderPaymentMethod(Number(paymentMethod.id));
    } catch (error) {
      showErrorModal();
      reportError(error);
    }
  }, [client, paymentSource, showErrorModal, updateOrderPaymentMethod]);

  const getSelectLatestAddressValidation = useCallback(() => {
    const orderSummaryQueryResult = client.readQuery<
      GetOrderSummaryQuery,
      GetOrderSummaryQueryVariables
    >({
      query: GET_ORDER_SUMMARY_QUERY,
      variables: { orderNumber, countryCode: country.alpha2Code }
    });
    const orderSummary = orderSummaryQueryResult?.getOrderSummary.data[0];

    if (!orderSummary?.attributes.isAddressChangeable) {
      return {
        canSelectLatestAddress: false,
        selectLatestAddressValidationMessage:
          'Your payment method has been successfully updated. You currently have items being processed for shipping so we are unable to update your shipping address at this time. Please contact Client Services with any questions.'
      };
    }

    const addresses = client.readQuery<AddressesQuery>({ query: ADDRESSES_QUERY });

    const address = addresses?.addresses.data.find(
      address => address.attributes.source === paymentSource.toLowerCase()
    );

    if (orderSummary?.attributes.restrictedAddressIds?.includes(Number(address?.id))) {
      return {
        canSelectLatestAddress: false,
        selectLatestAddressValidationMessage:
          "Your payment method has been successfully updated. Some of the items can't be shipped to the address you entered so we were unable to update your shipping address. Please contact Client Services with any questions."
      };
    }
    return {
      canSelectLatestAddress: true,
      latestAddress: address,
      selectLatestAddressValidationMessage: ''
    };
  }, [country.alpha2Code, orderNumber, client, paymentSource]);

  const selectLatestAddress = useCallback(async () => {
    const { selectLatestAddressValidationMessage } = getSelectLatestAddressValidation();

    // // read the addresses from the cache directly here instead of using the `useAddressesQuery` hook
    // // to avoid race conditions where this is called right after the address is created
    const addresses = client.readQuery<AddressesQuery>({ query: ADDRESSES_QUERY });

    const address = addresses?.addresses.data.find(
      address => address.attributes.source === paymentSource.toLowerCase()
    );

    if (selectLatestAddressValidationMessage) {
      showDialog({
        message: selectLatestAddressValidationMessage,
        buttonText: 'OK'
      });
      return;
    }

    try {
      if (!address) throw new Error('selectLatestAddress: no address found');
      await updateOrderShippingAddress(address.id);
    } catch (error) {
      showErrorModal();
      reportError(error);
    }
  }, [
    client,
    paymentSource,
    showErrorModal,
    updateOrderShippingAddress,
    showDialog,
    getSelectLatestAddressValidation
  ]);

  return {
    paymentSource,
    changePaymentSource,
    selectLatestPaymentMethod,
    selectLatestAddress,
    getSelectLatestAddressValidation
  };
};
